<template>
	<div class="notifications-bell">
		<v-badge
			color="#E22C3A"
			:model-value="!!notifications.length"
			:content="notifications.length"
		>
			<v-icon size="30">mdi-bell</v-icon>
		</v-badge>

		<!--
			Alert menu: shown automatically when there is an active notification and the
			list is closed. It is NOT opened by clicking the bell (open-on-click=false) and
			is only dismissed via its own action/dismiss button or its timeout, so clicking
			anywhere on the page keeps it visible.
		-->
		<v-menu
			activator="parent"
			:model-value="alertOpen"
			location="start center"
			offset="10"
			:width="600"
			:open-on-click="false"
			:close-on-content-click="false"
			no-click-animation
			persistent
			:content-class="{
				'notifications-container': true,
				'v-theme--dark': darkMode,
				'error-notification': true
			}"
		>
			<v-alert
				v-if="lastNotification"
				:type="lastNotificationType"
				icon="mdi-alert"
				:rounded="0"
				density="compact"
			>
				<template #prepend>
					<v-icon size="24" icon="mdi-alert" />
				</template>
				<template #default>
					<div class="text-caption">
						{{ lastNotification.message }}
					</div>
				</template>
				<template #append>
					<v-btn variant="text" size="small" class="float-end" @click.stop="notificationAction">
						{{ lastNotification.action?.text ?? "Dismiss" }}
					</v-btn>
				</template>
			</v-alert>
		</v-menu>

		<!--
			List menu: opened ONLY by clicking the bell (default activator behaviour) and
			closed by clicking outside. It is independent from the active alert state, so an
			active alert can never force it open and outside clicks always close it.
		-->
		<v-menu
			v-model="notificationsContainer.open"
			activator="parent"
			location="start center"
			offset="10"
			:width="600"
			:close-on-content-click="false"
			no-click-animation
			:content-class="{
				'notifications-container': true,
				'v-theme--dark': darkMode
			}"
		>
			<v-list class="pa-0">
				<div class="d-flex justify-space-between">
					<v-list-subheader class="font-weight-bold">NOTIFICATIONS</v-list-subheader>
					<v-list-subheader>
						<v-btn v-if="notifications.length > 0" variant="plain" color="secondary" @click="clearAll">
							Mark all as read
						</v-btn>
					</v-list-subheader>
				</div>
				<perfect-scrollbar
					class="ps"
					:class="[notificationsContainer.expanded && notifications.length > 6 ? 'scrollable' : '']"
				>
					<template v-for="(notification, index) in notifications">
						<v-list-item
							v-if="notificationsContainer.expanded || index < 3"
							:key="notification.id"
							:base-color="notificationType(notification)"
						>
							<template #prepend>
								<v-icon size="20" :icon="`mdi-${notificationType(notification)}`" />
							</template>
							<template #default>
								<div class="text-caption pl-2">
									{{ notification.message }}
								</div>
							</template>
							<template #append>
								<v-btn icon size="small" variant="text" @click="dismissNotification(notification)">
									<v-icon size="24">mdi-close</v-icon>
								</v-btn>
							</template>
						</v-list-item>
						<v-divider
							v-if="index < notifications.length - 1 && (notificationsContainer.expanded || index < 3)"
							:key="index"
						/>
					</template>
				</perfect-scrollbar>
				<v-btn
					v-if="notifications.length > 3 && !notificationsContainer.expanded"
					variant="plain"
					color="secondary"
					@click="notificationsContainer.expanded = true"
				>
					See more
				</v-btn>
			</v-list>
		</v-menu>
	</div>
</template>

<script>
/* Enums */
import {NotificationTypes} from "@/enums/NotificationTypes";

export default {
	name: "NotificationsContainer",

	data() {
		return {
			notificationsContainer: {
				open: false,
				expanded: false
			}
		};
	},

	computed: {
		notifications() {
			return this.$store.getters.getNotifications();
		},

		lastNotification() {
			return this.notifications[0];
		},

		lastNotificationType() {
			return NotificationTypes[this.lastNotification.type];
		},

		darkMode() {
			return this.$store.getters.darkMode();
		},

		alertOpen() {
			// The transient alert is shown when there is an active notification and the
			// notification list is closed. It is a one-way binding on purpose so that
			// outside clicks never dismiss it.
			return !!this.lastNotification?.active && !this.notificationsContainer.open;
		}
	},

	watch: {
		"notificationsContainer.open"(open) {
			if (open) {
				this.notificationsContainer.expanded = false;
				// Opening the list hides the active alert so it does not reappear on close.
				if (this.lastNotification?.active) {
					this.$store.commit("HIDE_NOTIFICATION", this.lastNotification);
				}
			}
		},

		notifications: {
			handler(notifications) {
				if (!notifications.length) {
					this.notificationsContainer = {
						open: false,
						expanded: false
					};
				}
			},
			deep: true
		}
	},

	mounted() {
		window.addEventListener("notificationEvent", this.onNotificationEvent);
	},

	beforeUnmount() {
		window.removeEventListener("notificationEvent", this.onNotificationEvent);
	},

	methods: {
		dismissNotification(notification) {
			this.$store.commit("REMOVE_NOTIFICATION", notification);
		},

		notificationAction() {
			if (this.lastNotification.action?.click) {
				this.lastNotification.action.click();
			}
			this.$store.commit("HIDE_NOTIFICATION", this.lastNotification);
		},

		onNotificationEvent(customEvent) {
			const {detail} = customEvent;
			if (detail.type === "error") {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", detail);
			} else {
				this.$store.commit("SHOW_SUCCESS_NOTIFICATION", detail);
			}
		},

		notificationType(notification) {
			return NotificationTypes[notification.type];
		},

		clearAll() {
			this.$store.commit("REMOVE_ALL_NOTIFICATIONS");
		}
	}
};
</script>
